import type { ReactNode } from 'react'
import {
  useAssignIssue,
  useAssignableDevelopers,
  useUpdateIssuePriority,
  useUpdateIssueStatus,
} from '../../hooks/useSystemIssues'
import {
  PRIORITY_LABELS,
  STATUS_LABELS,
  formatDateTime,
} from '../shared/IssueRow'
import type {
  SystemIssue,
  SystemIssuePriority,
  SystemIssueStatus,
} from '../../types/system-issues'

interface IssuePropertiesPanelProps {
  issue: SystemIssue
  /** Selects só para quem pode triagem (dev); os demais veem os valores em texto. */
  canManage: boolean
}

const selectClass =
  'w-full rounded-[5px] border border-border-subtle bg-surface px-2 py-1.5 text-xs text-body focus:border-blue-400 focus:outline-none disabled:cursor-wait disabled:opacity-60'

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-subtle">
        {label}
      </div>
      {children}
    </div>
  )
}

export function IssuePropertiesPanel({
  issue,
  canManage,
}: IssuePropertiesPanelProps) {
  const updateStatus = useUpdateIssueStatus(issue.id)
  const updatePriority = useUpdateIssuePriority(issue.id)
  const assignIssue = useAssignIssue(issue.id)
  const { data: developers = [] } = useAssignableDevelopers(canManage)

  const assignee = issue.assigned_to
  // Dev inativo some da lista de atribuíveis, mas segue sendo o atual.
  const assignedMissing =
    assignee != null && !developers.some((d) => d.id === assignee.id)

  return (
    <aside className="shrink-0 space-y-4 overflow-y-auto border-l border-border-subtle bg-surface px-6 py-3">
      <Field label="Atribuído a">
        {canManage ? (
          <select
            aria-label="Atribuído a"
            value={issue.assigned_to_id ?? ''}
            disabled={assignIssue.isLoading}
            onChange={(e) => assignIssue.mutate(Number(e.target.value))}
            className={selectClass}
          >
            {/* A API não desatribui — "Não atribuído" só vale como estado inicial. */}
            {issue.assigned_to_id == null && (
              <option value="" disabled>
                Não atribuído
              </option>
            )}
            {assignedMissing && (
              <option value={assignee.id}>{assignee.full_name}</option>
            )}
            {developers.map((dev) => (
              <option key={dev.id} value={dev.id}>
                {dev.first_name} {dev.last_name}
              </option>
            ))}
          </select>
        ) : (
          <div className="text-xs text-body">
            {assignee?.full_name ?? 'Não atribuído'}
          </div>
        )}
      </Field>

      <Field label="Status">
        {canManage ? (
          <select
            aria-label="Status"
            value={issue.status}
            disabled={updateStatus.isLoading}
            onChange={(e) =>
              updateStatus.mutate(e.target.value as SystemIssueStatus)
            }
            className={selectClass}
          >
            {Object.entries(STATUS_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        ) : (
          <div className="text-xs text-body">{STATUS_LABELS[issue.status]}</div>
        )}
      </Field>

      <Field label="Prioridade">
        {canManage ? (
          <select
            aria-label="Prioridade"
            value={issue.priority}
            disabled={updatePriority.isLoading}
            onChange={(e) =>
              updatePriority.mutate(e.target.value as SystemIssuePriority)
            }
            className={selectClass}
          >
            {Object.entries(PRIORITY_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        ) : (
          <div className="text-xs text-body">
            {PRIORITY_LABELS[issue.priority]}
          </div>
        )}
      </Field>

      <Field label="Data de abertura">
        <div className="text-xs text-body">
          {formatDateTime(issue.created_at)}
        </div>
      </Field>
    </aside>
  )
}
